import type { QueryClient } from '@tanstack/react-query';
import { hashDiagnosticEntity } from '../tx-engine/diagnostics/lifecycle.js';

export const WALLET_ASSET_ENTITY_QUERY_KEY = 'wallet-asset-entity';

export interface WalletAssetIdentity {
  readonly network: string;
  readonly wallet: string;
  readonly mint: string;
}

export type WalletAssetEntitySource = 'rpc' | 'trade_snapshot' | 'websocket' | 'confirmed_mutation';

/** JSON-safe exact token-account value shared by web and React Native caches. */
export interface WalletAssetEntity extends WalletAssetIdentity {
  readonly rawBalance: string;
  readonly slot: string;
  readonly fetchedAtMs: number;
  readonly source: WalletAssetEntitySource;
}

export interface WalletAssetEntityLogger {
  readonly warn: (message: string, context: Record<string, unknown>) => void;
}

export type SetWalletAssetEntityResult = 'updated' | 'stale_slot' | 'stale_timestamp';
export type PatchWalletAssetEntityResult = SetWalletAssetEntityResult | 'missing';

export function walletAssetEntityQueryKey(
  identity: WalletAssetIdentity,
): readonly [typeof WALLET_ASSET_ENTITY_QUERY_KEY, string, string, string] {
  return [WALLET_ASSET_ENTITY_QUERY_KEY, identity.network, identity.wallet, identity.mint];
}

/** Observe a canonical entity without creating a second RPC owner for it. */
export function walletAssetEntityObserverOptions(
  queryClient: QueryClient,
  identity: WalletAssetIdentity,
): {
  readonly queryKey: ReturnType<typeof walletAssetEntityQueryKey>;
  readonly queryFn: () => WalletAssetEntity;
  readonly enabled: false;
} {
  const queryKey = walletAssetEntityQueryKey(identity);
  return {
    queryKey,
    queryFn: (): WalletAssetEntity => {
      const entity = queryClient.getQueryData<WalletAssetEntity>(queryKey);
      if (entity === undefined) {
        throw new Error('Canonical wallet asset entity is unavailable');
      }
      return entity;
    },
    enabled: false,
  };
}

export function setWalletAssetEntity(
  queryClient: QueryClient,
  entity: WalletAssetEntity,
  logger?: WalletAssetEntityLogger,
): SetWalletAssetEntityResult {
  assertUnsignedInteger(entity.rawBalance, 'rawBalance');
  assertUnsignedInteger(entity.slot, 'slot');
  if (!Number.isSafeInteger(entity.fetchedAtMs) || entity.fetchedAtMs < 0) {
    throw new Error('Wallet asset fetchedAtMs must be a non-negative safe integer');
  }

  const queryKey = walletAssetEntityQueryKey(entity);
  const cached = queryClient.getQueryData<WalletAssetEntity>(queryKey);
  if (cached !== undefined) {
    const incomingSlot = BigInt(entity.slot);
    const cachedSlot = BigInt(cached.slot);
    if (incomingSlot < cachedSlot) {
      logger?.warn('wallet_asset.stale_slot_rejected', {
        ...diagnosticIdentity(entity),
        source: entity.source,
        incomingSlot: entity.slot,
        cachedSlot: cached.slot,
      });
      return 'stale_slot';
    }
    if (incomingSlot === cachedSlot && entity.fetchedAtMs < cached.fetchedAtMs) {
      return 'stale_timestamp';
    }
  }

  queryClient.setQueryData(queryKey, entity);
  return 'updated';
}

export function patchWalletAssetEntityBalance(
  queryClient: QueryClient,
  input: WalletAssetIdentity & { readonly rawDelta: bigint; readonly fetchedAtMs: number },
  logger?: WalletAssetEntityLogger,
): PatchWalletAssetEntityResult {
  const queryKey = walletAssetEntityQueryKey(input);
  const cached = queryClient.getQueryData<WalletAssetEntity>(queryKey);
  if (cached === undefined) {
    logger?.warn('wallet_asset.patch_missing', {
      ...diagnosticIdentity(input),
      source: 'confirmed_mutation',
    });
    return 'missing';
  }

  const { rawDelta, ...identity } = input;
  const sum = BigInt(cached.rawBalance) + rawDelta;
  return setWalletAssetEntity(
    queryClient,
    {
      ...identity,
      rawBalance: (sum < 0n ? 0n : sum).toString(),
      slot: cached.slot,
      source: 'confirmed_mutation',
    },
    logger,
  );
}

function diagnosticIdentity(identity: WalletAssetIdentity): {
  readonly network: string;
  readonly walletHash: string;
  readonly mintHash: string;
} {
  return {
    network: identity.network,
    walletHash: hashDiagnosticEntity(identity.wallet),
    mintHash: hashDiagnosticEntity(identity.mint),
  };
}

function assertUnsignedInteger(value: string, field: string): void {
  if (!/^(0|[1-9]\d*)$/.test(value)) {
    throw new Error(`Wallet asset ${field} must be an unsigned base-10 integer string`);
  }
}
